/* @flow */

import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_V8_SECTION } from '../../libs/netgemLibrary/v8/types/Section';
import type { BO_PURCHASE_LIST_TYPE } from '../../redux/netgemApi/actions/videofutur/types/purchase';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';

import { getLocationType } from '../../libs/netgemLibrary/v8/helpers/Item';
import { ItemLocationKind, type NETGEM_API_V8_FEED, type NETGEM_API_V8_FEED_ITEM, type NETGEM_API_V8_ITEM_LOCATION } from '../../libs/netgemLibrary/v8/types/FeedItem';
import { METADATA_KIND_PROGRAM, type NETGEM_API_V8_METADATA_PROGRAM } from '../../libs/netgemLibrary/v8/types/MetadataProgram';
import { type NETGEM_API_V8_WIDGET_ACTIONS_TYPE, NetgemApiV8WidgetItemClickActionKind } from '../../libs/netgemLibrary/v8/types/WidgetConfig';
import { RegistrationType } from '../../redux/appRegistration/types/types';
import sendV8CustomStrategyRequest from '../../redux/netgemApi/actions/v8/customStrategyRequest';
import sendV8SectionFeedRequest from '../../redux/netgemApi/actions/v8/feed';
import sendV8MetadataRequest from '../../redux/netgemApi/actions/v8/metadata';
import sendV8MetadataLocationRequest, { getVodLocations } from '../../redux/netgemApi/actions/v8/metadataLocation';
import { sendV8RecordingsMetadataRequest } from '../../redux/netgemApi/actions/v8/recordings';
import { APPLICATION_ID } from '../applicationCustomization/types';
import { logDebug, logError, logInfo, logWarning } from '../debug/debug';
import { LoadableStatus } from '../loadable/loadable';
import Localizer from '../localization';
import { Messenger, MessengerEvents } from '../messenger';
import { isVodItem } from '../ui/item/metadata';
import { getOnItemClick } from '../ui/item/traverser';
import { type EXTENDED_ITEM, ExtendedItemType } from '../ui/item/types';
import { loadEpisodeMetadataList, loadEpisodesTVLocationMetadata, loadEpisodesVodLocationMetadata } from '../ui/metadata/batchOperations';
import { type ContinuousWatchingData, VIEWINGHISTORY_VIEWED_THRESHOLD } from './constantsAndTypes';
import { getMostRecentlyPlayedEpisode, getMostRecentlyPlayedItem } from './ViewingHistory';

// If viewing history is not loaded at first, we retry every 200 ms
const VIEWINGHISTORY_LOAD_RETRY_DELAY: number = 200;

// But we bail out after 10 seconds (= 50 * 200 ms)
const VIEWINGHISTORY_LOAD_RETRY_MAX_COUNT: number = 50;

class ContinuousWatching {
  static dispatch: Dispatch;
  static getState: () => CombinedReducers;
  static actions: NETGEM_API_V8_WIDGET_ACTIONS_TYPE;

  static #internalFlag: symbol = Symbol('internal');

  #buttonText: string = '';
  #distributorId: string | null = null;
  #episodeIndex: number = 0; // 0-based
  #episodeNumber: number = 1; // 1-based
  #episodeList: NETGEM_API_V8_FEED | null = null;
  #episodeMetadataList: Array<NETGEM_API_V8_METADATA_PROGRAM> | null = null;
  #isResume: boolean = false;
  #isVod: boolean = false;
  #seasonNumber: number = -1; // 1-based (-1 means "not initialized" or no season specified)
  #seriesId: string | null = null;
  #status: LoadableStatus = LoadableStatus.NotInitialized;
  #tvLocationList: Array<NETGEM_API_V8_METADATA_SCHEDULE> | null = null;
  #vodLocationList: Array<NETGEM_API_V8_METADATA_SCHEDULE> | null = null;

  constructor(flag?: symbol) {
    if (flag !== ContinuousWatching.#internalFlag) {
      throw new Error('Do not directly call this constructor. Use "await ContinuousWatching.create()" instead.');
    }
  }

  /*
   * Getters for private members
   */

  get buttonText(): string {
    return this.#buttonText;
  }

  get isLastEpisode(): boolean {
    if (this.#episodeList === null) {
      return false;
    }

    if (this.#isVod) {
      // VOD series
      return this.#episodeIndex === this.#episodeList.length - 1;
    }

    // TV series
    return this.#episodeIndex === 0;
  }

  get isReady(): boolean {
    return this.#status === LoadableStatus.Loaded;
  }

  get shouldStartFromBeginning(): boolean {
    return !this.#isResume;
  }

  /*
   * Static methods
   */

  // $FlowFixMe[unsupported-syntax] Flow does not support symbols yet
  get [Symbol.toStringTag](): string {
    return 'ContinuousWatching';
  }

  static async create(item: NETGEM_API_V8_FEED_ITEM, seriesId: string): Promise<ContinuousWatching | null> {
    // TODO: Remove this code when everything works in all apps
    if (process.env.REACT_APP_ID !== APPLICATION_ID.FranceChannel) {
      // Current scope is limited to FranceChannel
      return null;
    }

    const instance = new ContinuousWatching(ContinuousWatching.#internalFlag);
    await instance.asyncConstructor(item, seriesId);
    return instance;
  }

  /*
   * Class methods
   */

  static initialize(actions: NETGEM_API_V8_WIDGET_ACTIONS_TYPE, dispatch: Dispatch, getState: () => CombinedReducers): void {
    ContinuousWatching.actions = actions;
    ContinuousWatching.dispatch = dispatch;
    ContinuousWatching.getState = getState;
  }

  updateButtonText() {
    if (this.#status === LoadableStatus.Loaded) {
      this.#buttonText = Localizer.localize(`${this.#isResume ? 'common.actions.resume_episode' : 'common.actions.watch_episode'}_${this.#isVod ? 'vod' : 'tv'}`, {
        number: this.getEpisodeNumberDisplay(),
      });
    } else {
      this.#buttonText = '';
    }

    Messenger.emit(MessengerEvents.CONTINUOUS_WATCHING_BUTTON_TEXT_UPDATED, this.#status === LoadableStatus.Loaded, this.#seriesId, this.#episodeIndex, this.#buttonText);
  }

  getEpisodeNumberDisplay(): string {
    if (this.#isVod) {
      // VOD series: only display episode number
      return this.#episodeNumber.toString();
    }

    // TV series: display season and number (SxxExx)
    return `S${this.#seasonNumber} E${this.#episodeNumber}`;
  }

  async asyncConstructor(item: NETGEM_API_V8_FEED_ITEM, seriesId: string): Promise<void> {
    if (!ContinuousWatching.actions.onItemClick) {
      logWarning('onItemClick not defined for continuous watching');
      return;
    }

    this.#isVod = isVodItem(item);
    this.#seriesId = seriesId;
    const fullItem: EXTENDED_ITEM = {
      item,
      type: this.#isVod ? ExtendedItemType.VOD : ExtendedItemType.TV,
    };

    // Find action
    const onItemClick = getOnItemClick(fullItem, ContinuousWatching.actions.onItemClick);

    if (onItemClick === null) {
      // No onItemClick directive found
      return;
    }

    const { action, params } = onItemClick;

    if (action !== NetgemApiV8WidgetItemClickActionKind.Card || params?.uri === undefined) {
      // No action found or unexpected type
      return;
    }

    // Get first section from card avenue
    const firstSection = await this.getFirstSection(params as Object as NETGEM_API_V8_URL_DEFINITION, item);

    if (firstSection === null) {
      // No section
      return;
    }

    this.#episodeList = await this.getSectionFeed(firstSection);

    if (this.#episodeList === null) {
      // No feed
      return;
    }

    // Get all episodes' metadata
    this.#episodeMetadataList = await this.getEpisodeMetadataList(this.#episodeList);

    if (this.#episodeMetadataList === null) {
      // No metadata
      return;
    }

    this.initializeResumeData();
  }

  hasBeenPurchased(purchaseList: BO_PURCHASE_LIST_TYPE, vtiId: string): boolean {
    for (const purchaseListByDistributor of Object.values(purchaseList)) {
      if (purchaseListByDistributor[vtiId] !== undefined) {
        return true;
      }
    }

    return false;
  }

  filterPurchasedLocationList(locations: Array<NETGEM_API_V8_METADATA_SCHEDULE> | null): Array<NETGEM_API_V8_METADATA_SCHEDULE> | null {
    const {
      ui: { purchaseList },
    } = ContinuousWatching.getState();

    if (locations === null) {
      return null;
    }

    if (!purchaseList) {
      // Purchase list not retrieved yet
      return locations;
    }

    if (getLocationType(locations[0].location.id) === ItemLocationKind.SVOD) {
      // SVOD is free (subscription feature)
      return locations;
    }

    return locations.filter((loc) => {
      const {
        location: { purchaseInfo },
      } = loc;
      if (!purchaseInfo) {
        return false;
      }

      return this.hasBeenPurchased(purchaseList, purchaseInfo.vtiId.toString());
    });
  }

  async getEpisodeMetadataList(feed: NETGEM_API_V8_FEED): Promise<Array<NETGEM_API_V8_METADATA_PROGRAM> | null> {
    try {
      const episodeIds: Array<string> = feed.map(({ id }) => id);
      return await loadEpisodeMetadataList(episodeIds, (id: string) => ContinuousWatching.dispatch(sendV8MetadataRequest(id, METADATA_KIND_PROGRAM)));
    } catch (error) {
      logInfo('Error retrieving episode locations');
      logError(error);
      return null;
    }
  }

  getLocationsListFromEpisodeList(): Array<Array<NETGEM_API_V8_ITEM_LOCATION>> {
    if (this.#episodeList === null) {
      return [];
    }

    const locationsList: Array<Array<NETGEM_API_V8_ITEM_LOCATION>> = [];
    for (const { locations } of this.#episodeList) {
      if (locations) {
        locationsList.push(locations);
      }
    }

    return locationsList;
  }

  async getVodLocationList(): Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE> | null> {
    if (this.#episodeList === null) {
      return null;
    }

    const allLocationMetadata = await loadEpisodesVodLocationMetadata(this.getLocationsListFromEpisodeList(), (locations: Array<NETGEM_API_V8_ITEM_LOCATION>, signal?: AbortSignal) =>
      ContinuousWatching.dispatch(getVodLocations(locations, signal)),
    );

    return this.filterPurchasedLocationList(allLocationMetadata);
  }

  async getTVLocationList(): Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE> | null> {
    if (this.#episodeList === null) {
      return null;
    }

    return await loadEpisodesTVLocationMetadata(
      this.getLocationsListFromEpisodeList(),
      (assetId: string, signal?: AbortSignal) => ContinuousWatching.dispatch(sendV8MetadataLocationRequest(assetId, signal)),
      (assetId: string, signal?: AbortSignal) => ContinuousWatching.dispatch(sendV8RecordingsMetadataRequest(assetId, signal)),
    );
  }

  async getLocationMetadataByProgramId(programId: string): Promise<NETGEM_API_V8_METADATA_SCHEDULE | null> {
    let locationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null = null;

    if (this.#isVod) {
      // VOD series
      if (this.#vodLocationList === null) {
        this.#vodLocationList = await this.getVodLocationList();
      }

      if (this.#vodLocationList === null) {
        logWarning("Error retrieving episodes' VOD location metadata");
        return null;
      }

      locationMetadata = this.#vodLocationList.find(({ location }) => location.programId === programId) ?? null;
    } else {
      // TV series
      if (this.#tvLocationList === null) {
        this.#tvLocationList = await this.getTVLocationList();
      }

      if (this.#tvLocationList === null) {
        logWarning("Error retrieving episodes' TV location metadata");
        return null;
      }

      const programIndex = this.#episodeList?.findIndex(({ id }) => id === programId) ?? -1;
      locationMetadata = programIndex > -1 && this.#tvLocationList !== null ? this.#tvLocationList[programIndex] : null;
    }

    if (locationMetadata === undefined || locationMetadata === null) {
      logWarning(`Cannot find ${this.#isVod ? 'VOD' : 'TV'} location metadata for program Id: ${programId}`);
      return null;
    }

    return locationMetadata;
  }

  async getFirstSection(urlDefinition: NETGEM_API_V8_URL_DEFINITION, item: NETGEM_API_V8_FEED_ITEM): Promise<NETGEM_API_V8_SECTION | null> {
    const response = await ContinuousWatching.dispatch(sendV8CustomStrategyRequest(urlDefinition, item));
    return response?.result.sections[0] ?? null;
  }

  async getSectionFeed(section: NETGEM_API_V8_SECTION): Promise<NETGEM_API_V8_FEED | null> {
    try {
      return await ContinuousWatching.dispatch(sendV8SectionFeedRequest(section));
    } catch (error) {
      logInfo('Error retrieving first section feed');
      logError(error);
      return null;
    }
  }

  setSeasonAndEpisodeNumbers(): void {
    const episodeOf = this.#episodeMetadataList?.[this.#episodeIndex].episodeOf;

    if (!episodeOf) {
      return;
    }

    const { episodeNumber, seasonNumber } = episodeOf;

    this.#seasonNumber = seasonNumber ?? -1;
    this.#episodeNumber = episodeNumber;

    logDebug(`[ContinuousWatching] Season: ${this.#seasonNumber} | Episode: ${this.#episodeNumber} | index: ${this.#episodeIndex} | isResume: ${this.#isResume.toString()}`);
  }

  initializeResumeDataInternal(episodeList: NETGEM_API_V8_FEED): boolean {
    const {
      ui: { viewingHistory },
    } = ContinuousWatching.getState();

    // Get series Id from feed item or metadata
    const seriesId = episodeList[0].seriesId ?? this.#seriesId;

    if (!seriesId) {
      // Cannot get series Id: error
      return false;
    }

    const { [seriesId]: seriesHistory } = viewingHistory;

    if (!seriesHistory) {
      // No viewing history for this series
      return false;
    }

    const { episodes } = seriesHistory;
    if (!episodes || episodes.length === 0) {
      // No episode has been viewed: this case shouldn't happen
      return false;
    }

    // Series tile: display the progress of the most recently played episode
    const mostRecentEpisode = getMostRecentlyPlayedEpisode(episodes);
    const { id: mostRecentEpisodeId, playeditems: playedItems } = mostRecentEpisode;

    // Get most recent item
    const mostRecentItem = playedItems ? getMostRecentlyPlayedItem(playedItems) : null;
    const percent = mostRecentItem?.percent ?? 0;

    // Get episode index and season count
    this.#episodeIndex = this.#episodeMetadataList?.findIndex((ep) => ep.id === mostRecentEpisodeId || ep.providerInfo?.viewingHistoryId === mostRecentEpisodeId) ?? 0;
    const episodeMetadata = this.#episodeMetadataList?.[this.#episodeIndex];
    if (!episodeMetadata) {
      // Episode not found in metadata list
      return false;
    }

    this.#isResume = percent > 0 && percent < VIEWINGHISTORY_VIEWED_THRESHOLD;

    if (percent >= VIEWINGHISTORY_VIEWED_THRESHOLD) {
      // Last viewed episode was entirely watched

      // Don't use episodeOf.count because it does not work when multiple seasons are merged (see https://netgem.atlassian.net/browse/GM-6758)
      const episodeCount = episodeList.length ?? 0;

      if (this.#episodeIndex < episodeCount - 1) {
        // If not last episode of season, play next one
        this.#episodeIndex += 1;
      } else {
        // Otherwise, restart from the first episode of season
        this.#isResume = false;
        this.#episodeIndex = 0;
      }
    }

    this.setSeasonAndEpisodeNumbers();
    return true;
  }

  /*
   * This method initialize the "next episode" data, and sets following variables:
   *  - #episodeIndex
   *  - #episodeNumber
   *  - #seasonNumber
   *  - #isResume
   */
  initializeResumeData(attempt: number = 1): void {
    const {
      appRegistration: { registration },
      ui: { viewingHistoryStatus },
    } = ContinuousWatching.getState();

    if (registration === RegistrationType.RegisteredAsGuest) {
      // Guest mode
      this.#seasonNumber = -1;
      this.#episodeNumber = 1;
      this.#episodeIndex = 0;
      this.#isResume = false;
      this.#buttonText = '';
      this.#status = LoadableStatus.Loaded;

      this.updateButtonText();
      return;
    }

    if (
      viewingHistoryStatus !== LoadableStatus.Loaded ||
      this.#episodeList === null ||
      this.#episodeList.length === 0 ||
      this.#episodeMetadataList === null ||
      this.#episodeMetadataList.length === 0
    ) {
      // Viewing history not loaded yet, or an error occurred, or it's empty, or there's no metadata for any episode
      if (viewingHistoryStatus === LoadableStatus.NotInitialized && attempt < VIEWINGHISTORY_LOAD_RETRY_MAX_COUNT) {
        setTimeout(() => this.initializeResumeData(attempt + 1), VIEWINGHISTORY_LOAD_RETRY_DELAY);
      }
      return;
    }

    // Default values
    this.#seasonNumber = -1;
    this.#episodeNumber = 1;
    this.#episodeIndex = 0;
    this.#isResume = false;
    this.#buttonText = '';
    this.#status = LoadableStatus.Loaded;

    if (!this.initializeResumeDataInternal(this.#episodeList) && !this.#isVod) {
      // TV series
      this.#episodeIndex = this.#episodeList.length - 1;
      this.setSeasonAndEpisodeNumbers();
    }

    this.updateButtonText();
  }

  async getData(): Promise<ContinuousWatchingData | null> {
    if (this.#episodeList === null || this.#episodeMetadataList === null) {
      logWarning('Missing episode list or metadata list');
      return null;
    }

    const item = this.#episodeList[this.#episodeIndex];
    const programMetadata = this.#episodeMetadataList[this.#episodeIndex];
    const viewingHistoryId = programMetadata.providerInfo?.viewingHistoryId ?? null;
    this.#distributorId = programMetadata.providerInfo?.distributorId ?? null;

    const locationMetadata = await this.getLocationMetadataByProgramId(programMetadata.id);

    if (locationMetadata === null) {
      logWarning('Missing location metadata');
      return null;
    }

    const {
      location: { providerInfo, purchaseInfo },
    } = locationMetadata;
    const vtiId = purchaseInfo?.vtiId ?? (providerInfo ? Number(providerInfo.id) : null);

    if (vtiId === null && (item.locType === ItemLocationKind.TVOD || item.locType === ItemLocationKind.EST)) {
      logWarning('Missing VTI Id');
      return null;
    }

    return {
      distributorId: this.#distributorId,
      index: this.#episodeIndex,
      item,
      locationMetadata,
      programMetadata,
      viewingHistoryId,
      vtiId,
    };
  }

  goToNextEpisode(): boolean {
    if (this.#episodeList === null || this.#episodeMetadataList === null) {
      logWarning('Missing episode list or metadata list');
      return false;
    }

    if (this.#isVod) {
      // VOD series: displayed from first to last
      if (this.#episodeIndex >= this.#episodeList.length) {
        logWarning('Cannot go to next episode because current one is the last');
        return false;
      }

      this.#episodeIndex += 1;
    } else {
      // TV series: displayed from last to first
      if (this.#episodeIndex === 0) {
        logWarning('Cannot go to next episode because current one is the last');
        return false;
      }

      this.#episodeIndex -= 1;
    }

    this.setSeasonAndEpisodeNumbers();
    this.#isResume = false;
    this.updateButtonText();
    return true;
  }

  // Parameter index is 0-based
  changeResumeEpisode(index: number): void {
    this.#episodeIndex = index;
    this.setSeasonAndEpisodeNumbers();
    this.#isResume = true;

    this.updateButtonText();
  }
}

export default ContinuousWatching;
